/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IImageProps, IImageSettings, Image } from '@msdyn365-commerce/core';
import { CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { ICheckoutShippingAddressResources } from '../checkout-shipping-address.props.autogenerated';

/**
 * The cartline product interface.
 */
export interface ICartLineWithProduct {
    cartLine: CartLine;
    product?: SimpleProduct;
}

/**
 * The checkout shipping cartline interface.
 */
export interface ICheckoutShippingCartLineInfo {
    lineId: string;
    imageProps: IImageProps;
    quantity: number;
}

/**
 * The checkout shipping cartline props.
 */
export interface ICheckoutShippingCartLinesProps {
    moduleClassName: string;
    cartLines: ICheckoutShippingCartLineInfo[];
    title: string;
    itemTitle: string;
    resources: ICheckoutShippingAddressResources;
}

// eslint-disable-next-line @typescript-eslint/naming-convention -- Following as per module
export const CheckoutPickupCartLines: React.FC<ICheckoutShippingCartLinesProps> = ({ moduleClassName, cartLines, title, itemTitle, resources }) => {
    if (!ArrayExtensions.hasElements(cartLines)) {
        return null;
    }
    const quantity = 1;
    return (
        <div className={`${moduleClassName}__group-images`}>
            <div className={`${moduleClassName}__group-images-header`}>
                <div className={`${moduleClassName}__group-images-header-main`}>
                    {title}
                </div>
                <div className={`${moduleClassName}__group-images-header-item`}>
                    {itemTitle}
                </div>
            </div>
            <div className={`${moduleClassName}__group-images-lines`}>
                {cartLines.map((cartLine: ICheckoutShippingCartLineInfo) => {
                    const isShowQty = cartLine.quantity > quantity;
                    let className = `${moduleClassName}__group-images-lines-product`;
                    if (isShowQty) {
                        className = classnames(className, ' product-image-wide');
                    }
                    return (
                        <div className={className} key={cartLine.lineId}>
                            <Image {...cartLine.imageProps} />
                            {isShowQty && <div
                                className={`${moduleClassName}__group-images-lines-line-quantity`}
                                aria-labelledby={cartLine.lineId}
                                aria-hidden='true'>
                                {cartLine.quantity}
                            </div>}
                            <span id={cartLine.lineId} className='msc-line__quantity sr-only'>
                                {format(resources.productQuantityInfo, cartLine.quantity, cartLine.imageProps.altText)}
                            </span>

                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * The checkout shipping cartline image settings.
 */
export const defaultImageSettings: IImageSettings = {
    viewports: {
        xs: { q: 'w=108&h=108&q=80&m=6&f=jpg', w: 108, h: 108 },
        lg: { q: 'w=108&h=108&q=80&m=6&f=jpg', w: 108, h: 108 },
        xl: { q: 'w=108&h=108&q=80&m=6&f=jpg', w: 108, h: 108 }
    },
    lazyload: true,
    quality: 80
};
